import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { AccountingEntry, AccountingMapping, TFetchStatus } from "./types";

/**
 * Lists exisisting typologies' names.
 */
export const fetchTypologies = createAsyncThunk("accounting/typologies", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ data: string[] }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/accounting/typologies",
        );
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

/**
 * Lists existing typologies.
 */
export const fetchEntries = createAsyncThunk("accounting/entries", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ data: AccountingEntry[] }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/accounting",
        );
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchOneEntry = createAsyncThunk(
    "accounting/oneEntry",
    async (id: string | number, { rejectWithValue }) => {
        try {
            const response = await axios.get<{ data: AccountingEntry }>(
                import.meta.env.REACT_APP_API_ENDPOINT + "/accounting/" + id,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchCreateEntry = createAsyncThunk(
    "accounting/createEntry",
    async (entry: AccountingEntry, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post<{ data: AccountingEntry }>(import.meta.env.REACT_APP_API_ENDPOINT + "/accounting", {
                    entry,
                }),
                {
                    pending: `${t("Accounting.EntryCreationPending")}`,
                    success: `${t("Accounting.EntryCreatedSuccessfully")}`,
                },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchUpdateEntry = createAsyncThunk(
    "accouting/updateEntry",
    async (entries: AccountingEntry[], { rejectWithValue }) => {
        try {
            const response = await axios.patch<{
                data: AccountingEntry | AccountingEntry[];
            }>(import.meta.env.REACT_APP_API_ENDPOINT + "/accounting", { entries });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchDeleteEntry = createAsyncThunk(
    "accouting/deleteEntry",
    async (entry: AccountingEntry, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.delete<{ data: string }>(import.meta.env.REACT_APP_API_ENDPOINT + "/accounting/" + entry.id),
                {
                    pending: `${t("Accounting.EntryDeletedPending")}`,
                    success: `${t("Accounting.EntryDeletedSuccessfully")}`,
                },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchDeleteEntryMapping = createAsyncThunk(
    "accouting/deleteEntryMapping",
    async (mapping: AccountingMapping, { rejectWithValue }) => {
        try {
            const response = await axios.delete<{ data: string }>(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/accounting/${mapping.entryId}/mapping/${mapping.id}`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

interface AccountingState {
    isLoading: boolean;
    entries: AccountingEntry[];
    typologies: string[];
    entry: AccountingEntry | null;
    typologiesStatus: TFetchStatus;
}

const initialState: AccountingState = {
    isLoading: false,
    entries: [],
    typologies: [],
    entry: null,
    typologiesStatus: "idle",
};

export const accountingSlice = createSlice({
    name: "accounting",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(fetchTypologies.fulfilled, (state, action) => {
            state.typologies = action.payload.data;
            state.typologiesStatus = "succeeded";
        });
        builder.addCase(fetchTypologies.pending, (state) => {
            state.typologiesStatus = "pending";
        });
        builder.addCase(fetchTypologies.rejected, (state, { payload }) => {
            state.typologiesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchEntries.fulfilled, (state, action) => {
            state.isLoading = false;
            state.entries = action.payload.data;
        });
        builder.addCase(fetchEntries.pending, (state, action) => {
            state.isLoading = true;
        });
        builder.addCase(fetchEntries.rejected, (state, { payload }) => {
            state.isLoading = false;
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchOneEntry.fulfilled, (state, action) => {
            state.entry = action.payload.data;
        });
        builder.addCase(fetchOneEntry.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchCreateEntry.fulfilled, (state, action) => {
            state.entries.push(action.payload.data);
            state.typologies.push(action.payload.data.name as string);
        });
        builder.addCase(fetchCreateEntry.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchUpdateEntry.fulfilled, (state, action) => {
            toast.success(`${t("Accounting.EntryUpdatedSuccessfully")}`);
            (action.payload.data as AccountingEntry[]).forEach((typology) => {
                const index = state.entries.findIndex((entry) => entry.id === typology.id);
                state.entries[index] = typology;
            });
        });
        builder.addCase(fetchUpdateEntry.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchDeleteEntryMapping.fulfilled, (state, action) => {
            toast.success(`${t("Accounting.EntryMappingDeletedSuccessfully")}`);
        });
        builder.addCase(fetchDeleteEntryMapping.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchDeleteEntry.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchDeleteEntry.fulfilled, (state, action) => {
            state.entries = state.entries.filter((entry) => entry.id !== action.meta.arg.id);
        });
    },
});

export default accountingSlice.reducer;
